import { useNavigate } from "react-router-dom";
import { IconArrowRight } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { PurchaseOrderDetailsDto } from "../../../../api";

const DATE_FMT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
function formatDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

function InfoCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Text variant="bodySm" tone="muted">{label}</Text>
      <Text variant="bodyBold" tone="dark">{value}</Text>
    </div>
  );
}

function LinkCell({ label, value, onClick }: { label: string; value: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex flex-col gap-0.5 text-left group">
      <Text variant="bodySm" tone="muted">{label}</Text>
      <div className="flex items-center gap-1.5">
        <Text variant="bodyBold" tone="accent" className="group-hover:underline">{value}</Text>
        <IconArrowRight size={14} className="text-textMuted opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
    </button>
  );
}

interface PurchaseOrderInfoCardProps {
  order: PurchaseOrderDetailsDto;
  isAdmin: boolean;
  buyerPartner?: { id: number; name: string } | null;
}

export function PurchaseOrderInfoCard({ order, isAdmin, buyerPartner }: PurchaseOrderInfoCardProps) {
  const navigate = useNavigate();

  const goToPartner = (id: number) =>
    navigate(AppRoutes.PARTNER_DETAILS.replace(":id", String(id)), {
      state: { backLabel: `← ${order.name}`, backPath: `${AppRoutes.ADMIN_PURCHASE_ORDER_DETAILS.replace(":id", String(order.id))}` },
    });

  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5 max-md:px-4">
      <Heading variant="h3" tone="dark">Order Information</Heading>
      <div className="grid grid-cols-4 gap-x-4 gap-y-5 max-md:grid-cols-2 border-t border-borderLight pt-4">
        {isAdmin && order.partner?.id ? (
          <LinkCell label="Seller" value={order.partner.name ?? "—"} onClick={() => goToPartner(order.partner!.id)} />
        ) : (
          <InfoCell label="Seller" value={order.partner?.name ?? "—"} />
        )}
        {isAdmin && buyerPartner?.id ? (
          <LinkCell label="Buyer" value={buyerPartner.name} onClick={() => goToPartner(buyerPartner.id)} />
        ) : buyerPartner?.name ? (
          <InfoCell label="Buyer" value={buyerPartner.name} />
        ) : null}
        <InfoCell label="Order Date" value={formatDate(order.dateOrder)} />
        <InfoCell label="Purchase Order" value={order.name} />
        <InfoCell label="Planned Date" value={formatDate(order.datePlanned)} />
        <InfoCell label="Status" value={order.state ?? "—"} />
      </div>
    </div>
  );
}
